import type { IUserProfile } from 'src/types/user';

import { onAuthStateChanged } from 'firebase/auth';
import { useMemo, useEffect, useCallback } from 'react';

import { useSetState } from 'src/hooks/use-set-state';

import axios from 'src/utils/axios';

import { AUTH } from 'src/lib/firebase';
import { useGetUser } from 'src/actions/user';

import { AuthContext } from '../auth-context';

import type { AuthState } from '../../types';

// ----------------------------------------------------------------------
type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({
    user: null,
    userProfile: null,
    loading: true,
    employerOrgs: null,
  });

  const { getUser } = useGetUser();

  const refreshToken = useCallback(async (user: AuthState['user']) => {
    if (user) {
      const token = await user.getIdToken(true);
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }
  }, []);

  const checkUserSession = useCallback(async () => {
    try {
      onAuthStateChanged(AUTH, async (user: AuthState['user']) => {
        if (user) {
          const { accessToken, uid } = user;

          axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

          // Refresh token periodically
          const tokenRefreshInterval = setInterval(() => refreshToken(user), 25 * 60 * 1000); // Refresh every 25 minutes

          // Handle page visibility changes to refresh token when tab becomes active
          const handleVisibilityChange = () => {
            if (!document.hidden && user) {
              // Tab became visible, refresh token immediately
              refreshToken(user);
            }
          };

          document.addEventListener('visibilitychange', handleVisibilityChange);

          // Cleanup function
          const cleanup = () => {
            clearInterval(tokenRefreshInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
          };

          // Store cleanup function for later use
          (user as any).__cleanup = cleanup;

          // Only fetch profile if it's not already loaded or if the user has changed.
          // Keep `loading` true until the profile resolves, otherwise guards and
          // permission-gated pages can treat an authorized user as unauthorized on
          // refresh/deep link before the profile arrives.
          if (!state.userProfile || state.user?.uid !== uid) {
            try {
              const userProfile = await getUser({ authId: uid });
              setState({
                user,
                userProfile,
                employerOrgs: userProfile?.employerOrgs,
                loading: false,
              });
            } catch (error) {
              console.error(error);
              setState({ user, loading: false });
            }
          } else {
            setState({ user, loading: false });
          }
        } else {
          // Clean up previous user's intervals and listeners
          if (state.user && (state.user as any).__cleanup) {
            (state.user as any).__cleanup();
          }

          setState({ user: null, loading: false, userProfile: null });
          delete axios.defaults.headers.common.Authorization;
        }
      });
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false, userProfile: null });
    }
  }, [setState, getUser, state.userProfile, state.user, refreshToken]);

  useEffect(() => {
    checkUserSession();
  }, [checkUserSession]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const setUserProfile = useCallback(
    (userProfile: IUserProfile | null) => {
      setState({ ...state, userProfile });
    },
    [setState, state]
  );

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            uid: state.user?.uid,
          }
        : null,
      userProfile: state.userProfile,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      setUserProfile,
      employerOrgs: state.employerOrgs,
    }),
    [checkUserSession, state.user, state.userProfile, status, setUserProfile, state.employerOrgs]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
